import type { PlanFeature } from './plans';

/**
 * What a workspace shows, and what each person in it sees.
 *
 * The product grew past what most teams open. Most of them track time, keep a
 * calendar of the week and book leave; the task tree, the planning tools and
 * the money are for some teams, some of the time. So the areas beyond time are
 * modules an admin switches on, the way Harvest's Settings → Modules works, and
 * a person can be narrowed further — a contractor who sees the timesheet and
 * nothing else is one click.
 *
 * This is visibility, not permission. Roles and the security rules decide what
 * a person may read and write, exactly as before; a module that is off hides
 * its pages, links and controls, and keeps its data. Switching it back on
 * brings everything back as it was.
 *
 * Time tracking is not a module. It is the product: the timer, the timesheet
 * and the hours reports are always there.
 */

export const MODULES = ['calendar', 'leave', 'tasks', 'clients', 'team', 'planning', 'finance'] as const;
export type Module = (typeof MODULES)[number];

export function isModule(value: unknown): value is Module {
  return typeof value === 'string' && (MODULES as readonly string[]).includes(value);
}

/** The three starting points. A workspace whose switches match none of them is `custom`. */
export const WORKSPACE_MODES = ['simple', 'standard', 'full'] as const;
export type WorkspaceMode = (typeof WORKSPACE_MODES)[number];

export const MODE_MODULES: Record<WorkspaceMode, readonly Module[]> = {
  simple: ['calendar', 'leave'],
  standard: ['calendar', 'leave', 'tasks', 'clients', 'team'],
  full: MODULES,
};

export function isWorkspaceMode(value: unknown): value is WorkspaceMode {
  return typeof value === 'string' && (WORKSPACE_MODES as readonly string[]).includes(value);
}

/**
 * A requested module list, made safe: unknown names dropped, duplicates
 * collapsed, stored in the table's order so two equal sets compare equal.
 */
export function cleanModules(requested: readonly unknown[] | null | undefined): Module[] {
  const set = new Set((requested ?? []).filter(isModule));
  return MODULES.filter((m) => set.has(m));
}

/** Which preset a set of modules is, if any. */
export function modeOf(modules: readonly Module[]): WorkspaceMode | 'custom' {
  const clean = cleanModules(modules);
  for (const mode of WORKSPACE_MODES) {
    const preset = MODE_MODULES[mode];
    if (preset.length === clean.length && preset.every((m) => clean.includes(m))) return mode;
  }
  return 'custom';
}

/**
 * The workspace's switches. Absent on every workspace that predates them,
 * which reads as everything on: a team that has been using the planner does
 * not open it one morning to find it gone.
 */
export function workspaceModules(org: { modules?: readonly string[] | null } | null | undefined): Module[] {
  if (!org || !Array.isArray(org.modules)) return [...MODULES];
  return cleanModules(org.modules);
}

/**
 * What one person sees: the workspace's modules, narrowed by the person's
 * own list when an admin set one. Null or absent on the membership follows
 * the workspace, so switching a module on reaches everyone who was not
 * deliberately narrowed. The owner always sees the whole workspace — they are
 * the one who would have to switch it back.
 */
export function personModules(
  org: { modules?: readonly string[] | null } | null | undefined,
  member: { role?: string; modules?: readonly string[] | null } | null | undefined,
): Module[] {
  const workspace = workspaceModules(org);
  if (!member || member.role === 'owner' || !Array.isArray(member.modules)) return workspace;
  const mine = new Set(cleanModules(member.modules));
  return workspace.filter((m) => mine.has(m));
}

/**
 * One-click access for a person. `everything` is null on the membership —
 * whatever the workspace has, now and later.
 */
export const ACCESS_PRESETS = ['everything', 'timeLeave', 'timeOnly'] as const;
export type AccessPreset = (typeof ACCESS_PRESETS)[number];

export const ACCESS_PRESET_MODULES: Record<AccessPreset, readonly Module[] | null> = {
  everything: null,
  timeLeave: ['calendar', 'leave'],
  timeOnly: [],
};

/** Which preset a membership's list is, or `custom`. */
export function accessPresetOf(modules: readonly string[] | null | undefined): AccessPreset | 'custom' {
  if (!Array.isArray(modules)) return 'everything';
  const clean = cleanModules(modules);
  for (const preset of ACCESS_PRESETS) {
    const list = ACCESS_PRESET_MODULES[preset];
    if (list && list.length === clean.length && list.every((m) => clean.includes(m))) return preset;
  }
  return 'custom';
}

/**
 * The module each plan feature lives in. Null is a feature of the workspace
 * itself — branding, exports, the audit log — which no module hides.
 *
 * Exhaustive on purpose: a new plan feature does not compile until somebody
 * has decided where it belongs.
 */
export const FEATURE_MODULE: Record<PlanFeature, Module | null> = {
  // Workspace-wide, never hidden.
  reportsAll: null,
  csvExport: null,
  branding: null,
  emailNotifications: null,
  weeklyDigest: null,
  scheduledReports: null,
  auditLog: null,
  fullExport: null,
  // Calendar: the planner and the calendars read into it.
  calendar: 'calendar',
  // Leave.
  holidays: 'leave',
  bulkAllowances: 'leave',
  leaveAwareWork: 'leave',
  // Tasks.
  savedViews: 'tasks',
  history: 'tasks',
  watchers: 'tasks',
  recurrence: 'tasks',
  relationships: 'tasks',
  taskImport: 'tasks',
  taskExport: 'tasks',
  dueReminders: 'tasks',
  automations: 'tasks',
  intakeForms: 'tasks',
  projectTemplates: 'tasks',
  // Clients.
  clientLinks: 'clients',
  retainers: 'clients',
  // Team.
  teamHub: 'team',
  lineup: 'team',
  activityFeed: 'team',
  // Planning.
  capacity: 'planning',
  budgets: 'planning',
  milestones: 'planning',
  projectHealth: 'planning',
  calibration: 'planning',
  runway: 'planning',
  goals: 'planning',
  burndown: 'planning',
  timeline: 'planning',
  dependencies: 'planning',
  brief: 'planning',
  honestDates: 'planning',
  // Finance.
  finance: 'finance',
};

/**
 * The pages each module owns, as org-relative path prefixes. A page not
 * listed here — the dashboard, time, the timesheet, reports, a task —
 * belongs to time tracking and is always reachable.
 *
 * `/projects` is special: the list of projects is the tasks module for a
 * member, but whoever sets up the work needs it in every setup, because time
 * is logged against a project's tasks. `projectsForManagers` says so, and
 * covers making a task for the same reason.
 */
export const MODULE_PATHS: readonly { prefix: string; module: Module; projectsForManagers?: true }[] = [
  { prefix: '/my-tasks', module: 'tasks' },
  { prefix: '/projects', module: 'tasks', projectsForManagers: true },
  { prefix: '/tasks/new', module: 'tasks', projectsForManagers: true },
  { prefix: '/planner', module: 'calendar' },
  { prefix: '/leave', module: 'leave' },
  { prefix: '/clients', module: 'clients' },
  { prefix: '/team', module: 'team' },
  { prefix: '/capacity', module: 'planning' },
  { prefix: '/goals', module: 'planning' },
  { prefix: '/finance', module: 'finance' },
];

/** The module a path belongs to, or null for a page that is always there. */
export function moduleForPath(path: string): (typeof MODULE_PATHS)[number] | null {
  const bare = path.split(/[?#]/)[0] ?? path;
  return MODULE_PATHS.find((entry) => bare === entry.prefix || bare.startsWith(`${entry.prefix}/`)) ?? null;
}

/** Whether a person who sees `modules` may open `path`. */
export function pathVisible(path: string, modules: readonly Module[], managesWork: boolean): boolean {
  const entry = moduleForPath(path);
  if (!entry) return true;
  if (entry.projectsForManagers && managesWork) return true;
  return modules.includes(entry.module);
}
